import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';
import { Body, Button, Eyebrow, I, Row, Stack } from '@leanin/ui';

import { GroupLogoBlock } from './GroupLogoBlock';
import {
  useCommitGroupImage,
  useRequestGroupImageUpload,
  type GroupImageContentType,
  type GroupImageField,
} from '../../lib/mutations/groups';
import { uploadWithProgress } from '../../lib/upload';

const ACCEPTED_TYPES: ReadonlyArray<GroupImageContentType> = [
  'image/jpeg',
  'image/png',
  'image/webp',
];
const MAX_BYTES = 10 * 1024 * 1024;

// Shared upload affordance for circle + network + space leaders. Handles
// both the brand-block logo and the wide cover image.
//
// Deferred-commit shape: picking a file only *stages* the upload — it
// renders a local blob-URL preview and dirties the field, but the
// presign → PUT → server commit does NOT run yet. The parent form's
// "Save changes" button stays in charge: when the user clicks it, the
// parent calls `commit()` on this field's ref, which then runs the
// upload pipeline and clears the pending state.
//
// Spaces only support `cover` (no logo) — they're topics, not brands.
export type GroupImageUploadFieldProps =
  | {
      readonly groupKind: 'circle' | 'network';
      readonly groupId: string;
      readonly groupName: string;
      readonly field: GroupImageField;
      readonly currentImageUrl: string | null;
      /** Notifies the parent when the field's dirty state flips, so it
       *  can include the image in its own dirty calc. */
      readonly onPendingChange?: (hasPending: boolean) => void;
      /** Override for the eyebrow label. Defaults to the field-typed
       *  label from `LABELS` ("Logo" / "Cover"). Pass when the caller
       *  wants a more specific phrasing (e.g. "Circle cover image"). */
      readonly eyebrowOverride?: string;
    }
  | {
      readonly groupKind: 'space';
      readonly id: string;
      readonly groupName: string;
      readonly field: GroupImageField;
      readonly currentImageUrl: string | null;
      readonly onPendingChange?: (hasPending: boolean) => void;
      readonly eyebrowOverride?: string;
    };

export type GroupImageUploadFieldHandle = {
  readonly hasPending: boolean;
  /** Runs presign → PUT → server commit for the staged file, then
   *  clears the pending state. No-op when nothing is staged. Rejects
   *  if the upload or commit fails — the caller's save handler should
   *  await this before its own update, and treat a rejection as a
   *  Save failure.
   *
   *  `identifierOverride` is for callers (e.g. /circles/new) where the
   *  group id only exists *after* the parent's create mutation
   *  resolves. The parent can pass the freshly-returned id without
   *  having to wait for a React re-render of this field's `groupId`
   *  prop. When omitted, falls back to the prop-derived identifier. */
  readonly commit: (identifierOverride?: string) => Promise<void>;
  /** Discards the staged file (revokes its blob URL). Used by the
   *  parent's Cancel path. */
  readonly reset: () => void;
};

export const GroupImageUploadField = forwardRef<
  GroupImageUploadFieldHandle,
  GroupImageUploadFieldProps
>(function GroupImageUploadField(props, ref) {
  const { groupKind, groupName, field, currentImageUrl, onPendingChange } = props;
  const identifier = props.groupKind === 'space' ? props.id : props.groupId;
  const t = useTranslations('Group');
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progressPct, setProgressPct] = useState<number | null>(null);
  // Server-committed URL: set after a successful commit() so the local
  // preview survives parent refetches landing momentarily later.
  const [committedUrl, setCommittedUrl] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingFileType, setPendingFileType] = useState<GroupImageContentType | null>(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(null);

  const requestMutation = useRequestGroupImageUpload(groupKind, field);
  const commitMutation = useCommitGroupImage(groupKind, field);
  const isUploading =
    requestMutation.isPending ||
    commitMutation.isPending ||
    progressPct !== null;
  // Display priority: staged blob (pending) > newly-committed URL > the
  // server's existing image > nothing.
  const displayedUrl = pendingPreviewUrl ?? committedUrl ?? currentImageUrl;

  // Revoke the blob URL whenever it changes or the component unmounts.
  // Holding on to revoked blob handles leaks memory on long-lived pages.
  useEffect(() => {
    return () => {
      if (pendingPreviewUrl !== null) URL.revokeObjectURL(pendingPreviewUrl);
    };
  }, [pendingPreviewUrl]);

  const stage = (file: File) => {
    const fileType = file.type as GroupImageContentType;
    if (!ACCEPTED_TYPES.includes(fileType)) {
      setError(t('imageUpload.badType', { field }));
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(t('imageUpload.tooBig', { field }));
      return;
    }
    setError(null);
    // Replace any earlier staged file's blob URL.
    if (pendingPreviewUrl !== null) URL.revokeObjectURL(pendingPreviewUrl);
    const nextUrl = URL.createObjectURL(file);
    setPendingFile(file);
    setPendingFileType(fileType);
    setPendingPreviewUrl(nextUrl);
    onPendingChange?.(true);
  };

  const reset = () => {
    if (pendingPreviewUrl !== null) URL.revokeObjectURL(pendingPreviewUrl);
    setPendingFile(null);
    setPendingFileType(null);
    setPendingPreviewUrl(null);
    setError(null);
    onPendingChange?.(false);
  };

  const commit = async (identifierOverride?: string): Promise<void> => {
    if (pendingFile === null || pendingFileType === null) return;
    const effectiveIdentifier = identifierOverride ?? identifier;
    setError(null);
    try {
      const presign = await requestMutation.mutateAsync({
        identifier: effectiveIdentifier,
        contentType: pendingFileType,
      });
      setProgressPct(0);
      await uploadWithProgress({
        url: presign.uploadUrl,
        body: pendingFile,
        headers: presign.fields,
        onProgress: (pct) => setProgressPct(pct),
      });
      const updated = await commitMutation.mutateAsync({
        identifier: effectiveIdentifier,
        key: presign.key,
      });
      const nextUrl =
        field === 'logo' && 'logoUrl' in updated
          ? updated.logoUrl
          : updated.imageUrl;
      setCommittedUrl(nextUrl ?? null);
      // Tear down the staged blob — the committed URL takes over.
      if (pendingPreviewUrl !== null) URL.revokeObjectURL(pendingPreviewUrl);
      setPendingFile(null);
      setPendingFileType(null);
      setPendingPreviewUrl(null);
      onPendingChange?.(false);
      void qc.invalidateQueries();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t('imageUpload.uploadFailed', { field });
      setError(message);
      // Re-throw so the parent's Save handler sees the failure and can
      // abort its own update / show its own error state.
      throw err instanceof Error ? err : new Error(message);
    } finally {
      setProgressPct(null);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      hasPending: pendingFile !== null,
      commit,
      reset,
    }),
    // Disabling because `commit` / `reset` close over many states that
    // already drive re-renders; the imperative handle is read at call
    // time by the parent. Keeping the deps array minimal avoids stale
    // captures while not over-invalidating.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingFile, pendingFileType, pendingPreviewUrl],
  );

  const pick = () => {
    setError(null);
    fileInputRef.current?.click();
  };

  const preview = field === 'logo'
    ? (
      <GroupLogoBlock
        logoUrl={displayedUrl}
        groupId={identifier}
        name={groupName}
        size={72}
      />
    )
    : displayedUrl !== null ? (
      <div
        style={{
          width: '100%',
          height: 220,
          borderRadius: 'var(--radius-md)',
          background: `url(${displayedUrl}) center/cover no-repeat`,
          border: '1px solid var(--border)',
        }}
        role="img"
        aria-label={t('imageUpload.coverAria', { name: groupName })}
      />
    ) : (
      <Stack
        gap="none"
        align="center"
        justify="center"
        style={{
          height: 220,
          borderRadius: 'var(--radius-md)',
          background: 'var(--muted)',
          border: '1px solid var(--border)',
        }}
      >
        <Body size="sm" color="mute">{t('imageUpload.noCover')}</Body>
      </Stack>
    );

  const statusLabel = isUploading
    ? progressPct !== null
      ? t('imageUpload.uploading', { pct: progressPct })
      : commitMutation.isPending
        ? t('imageUpload.finalising')
        : t('imageUpload.preparing')
    : pendingFile !== null
      ? t('imageUpload.pending')
      : null;

  return (
    <Stack gap="md">
      <Row gap="md" align="center" justify="space-between" wrap>
        <Eyebrow>{props.eyebrowOverride ?? t('imageUpload.fieldLabel', { field })}</Eyebrow>
        <Button
          variant="secondary"
          size="sm"
          icon={I.upload}
          onClick={pick}
          disabled={isUploading}
        >
          {displayedUrl !== null ? t('imageUpload.replace') : t('imageUpload.upload')}
        </Button>
      </Row>
      {field === 'logo' ? (
        <Row gap="md" align="center">
          {preview}
          {statusLabel !== null ? (
            <Body size="sm" color="mute">{statusLabel}</Body>
          ) : null}
          {error !== null ? (
            <Body size="sm" color="destructive">
              {error}
            </Body>
          ) : null}
        </Row>
      ) : (
        <Stack gap="sm">
          {preview}
          {statusLabel !== null ? (
            <Body size="sm" color="mute">{statusLabel}</Body>
          ) : null}
          {error !== null ? (
            <Body size="sm" color="destructive">
              {error}
            </Body>
          ) : null}
        </Stack>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) stage(file);
        }}
      />
    </Stack>
  );
});
